// REACT
import { useState } from "react";
import { useDispatch } from "react-redux";

// MATERIAL UI
import { CircularProgress, Grid, InputAdornment, Modal } from "@mui/material";

// COMPONENTS
import {
  StyledWithdrawalBox,
  StyledTitleContainer,
  StyledModalTitle,
  StyledMainContainer,
  StyledExplainerTextTypography,
  StyledFormContainer,
  StyledSecurityNoticeTypography,
  StyledStepHeaderTypography,
} from "../wallets/common/styles/CryptoWithdrawalStyled";
import { FormInput, InputStyleType } from "../input/FormInput";
import { StyledError } from "../wallets/Modal/Withdraw/Error";
import { AlertCard } from "../alerts/AlertCard";

// ICONS
import { Close } from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

// REDUX
import {
  useSendEmailCodeMutation,
  TwoFaEmailVerificationDto,
  useCheck2FaEmailCodeMutation,
} from "src/store/features/api/generated";

// SLICES
import {
  setEmailCode,
  setIsEmailCodeSuccess,
  set2FACode,
  reset2FAStateThunk,
} from "src/store/features/user.slice";
import { useUserSlice } from "src/store/features/user.slice";

// UTILS
import { truncateEmail } from "src/utils/helpers/EmailTruncate";
import { getSessionItem } from "src/utils/SessionManagement";

// AMPLITUDE
import { trackEvent } from "src/Amplitude";

interface I2FAModal {
  children: JSX.Element | JSX.Element[];
  title?: string;
  isOpen: boolean;
  emailTemplate: TwoFaEmailVerificationDto["emailTemplate"];
  handleClose: () => void;
  setError: (message: string) => void;
  isLoading: boolean;
}

/**
 * Reusable component to check an email code and an auth 2FA code.
 * @param {JSX.Element | JSX.Element[]} children - The child elements passed into the 2FA modal.
 * @param {string} title - The title of the modal.
 * @param {boolean} isOpen - A boolean determining wheter the modal is open or not.
 * @param {TwoFaEmailVerificationDto["emailTemplate"]} emailTemplate - The desired email template for the resent email click event.
 * @param {() => void} handleClose - The function handling the closing logic of the modal.
 * @param {(string) => void} setError - The function handling the error state of the modal.
 * @param {boolean} isLoading - A boolean determining whether the 2FA is loading or not.
 * @returns {JSX.Element} The TwoFA Modal.
 */
export const TwoFAModal = ({
  children,
  title,
  isOpen,
  emailTemplate,
  handleClose,
  setError,
  isLoading,
}: I2FAModal): JSX.Element => {
  const dispatch = useDispatch();

  /***************
   * LOCAL STATE *
   ***************/
  const [emailVerified, setEmailVerified] = useState<Boolean>(false);
  const [email2FAError, setEmail2FAError] = useState<string>("");

  /**********
   * SLICES *
   **********/
  const { emailCode, code2fa, isEmailCodeSuccess, is2FAEnabled } = useUserSlice();

  /***********
   * SESSION *
   ***********/
  const currentEmail = getSessionItem("email");

  /*******
   * API *
   *******/
  const [sendEmail] = useSendEmailCodeMutation();
  const [
    checkEmailCode,
    { isError: checkEmailIsError, isLoading: emailCheckLoading, reset: resetEmailCode },
  ] = useCheck2FaEmailCodeMutation();

  /************
   * HANDLERS *
   ************/
  const handleEmailChange = async (e: any) => {
    setEmail2FAError("");
    const inputCode = e.target.value;
    dispatch(setEmailCode(inputCode));
    if (e.target.value.length === 6) {
      try {
        await checkEmailCode({
          twoFaVerificationDto: {
            emailCode: e.target.value,
          },
        })
          .unwrap()
          .then(async (resp) => {
            setEmailVerified(true);
            dispatch(setIsEmailCodeSuccess(true));
            trackEvent("User validated 2FA email code", { email: currentEmail }, "2FA Modal");
          });
      } catch (error: any) {
        setEmail2FAError(error.message);
        trackEvent("Failed to validate 2FA email code", { email: currentEmail }, "2FA Modal");
      }
    }
  };

  const handle2FaChange = async (e: any) => {
    dispatch(setError(""));
    const inputCode = e.target.value;
    if (inputCode?.length <= 6) {
      dispatch(set2FACode(inputCode));
    }
  };

  const handleResendClick = async () => {
    dispatch(setEmailCode(""));
    resetEmailCode();
    await sendEmail({
      twoFaEmailVerificationDto: {
        emailTemplate: emailTemplate,
      },
    })
      .unwrap()
      .then(() => {
        trackEvent("Resending Email Verification Code", { email: currentEmail }, "2FA Modal");
      })
      .catch((error) => {
        trackEvent(
          "Error Trying To Resend Email Verification Code",
          { email: currentEmail },
          "2FA Modal",
        );
      });
  };

  const handleCloseModal = () => {
    setEmail2FAError("");
    dispatch(reset2FAStateThunk());
    resetEmailCode();
    handleClose();
  };

  /********
   * VARS *
   ********/
  const isDisabled = isEmailCodeSuccess || emailCheckLoading || !is2FAEnabled;
  const is2FADisabled = !is2FAEnabled || !isEmailCodeSuccess;
  const emailVerificationError = email2FAError?.includes("Your email verification code");

  return (
    <Modal open={isOpen} onClose={handleCloseModal}>
      <StyledWithdrawalBox>
        <StyledTitleContainer>
          <StyledModalTitle sx={{ flexGrow: 1, textAlign: "center" }}>{title}</StyledModalTitle>
          <Close onClick={handleCloseModal} style={{ cursor: "pointer" }} />
        </StyledTitleContainer>
        <StyledMainContainer>
          {!is2FAEnabled && (
            <AlertCard
              titleText={"Action Required"}
              subText={"It looks like you don’t have 2FA enabled, click"}
              link={"/settings/security"}
              linkText={"here"}
            />
          )}
          <Grid container direction="column" py={1} pb={4}>
            <StyledSecurityNoticeTypography>
              As an added layer of security, we have a 2-step process that you will need to complete
              to proceed with your request.
            </StyledSecurityNoticeTypography>
            <StyledFormContainer>
              <StyledStepHeaderTypography>Step 1: Email Verification</StyledStepHeaderTypography>
              <StyledExplainerTextTypography>
                We sent a verification code to your registered email address{" "}
                {currentEmail && truncateEmail(currentEmail)}
              </StyledExplainerTextTypography>
              <FormInput
                placeholder="000000"
                value={emailCode || ""}
                onChange={handleEmailChange}
                disabled={isDisabled}
                styleType={checkEmailIsError ? InputStyleType.ERROR : InputStyleType.DEFAULT}
                InputAdornment={
                  <InputAdornment position="end">
                    {emailCheckLoading ? (
                      <CircularProgress size={20} />
                    ) : isEmailCodeSuccess ? (
                      <CheckCircleIcon sx={{ color: "#0BEC9F", height: "22px" }} />
                    ) : null}
                  </InputAdornment>
                }
              />
              {checkEmailIsError && (
                <StyledError fontSize="12px">
                  {emailVerificationError
                    ? email2FAError + " Please try again, or resend code "
                    : email2FAError}
                  {emailVerificationError ? (
                    <span
                      style={{ fontSize: "12px", color: "CC0000", textDecoration: "underline" }}
                      onClick={handleResendClick}>
                      here
                      <span>.</span>
                    </span>
                  ) : (
                    <></>
                  )}
                </StyledError>
              )}
              {
                <div
                  style={{
                    filter: !emailVerified ? "blur(2px)" : "none", // Apply blur effect when emailVerified is false
                    pointerEvents: !emailVerified ? "none" : "auto", // Disable pointer events when emailVerified is false
                  }}>
                  <StyledStepHeaderTypography sx={{ marginTop: "16px" }}>
                    Step 2: Two Factor Verification
                  </StyledStepHeaderTypography>
                  <StyledExplainerTextTypography>
                    Enter the 2FA code from your authenticator
                  </StyledExplainerTextTypography>
                  <FormInput
                    placeholder="000000"
                    value={code2fa || ""}
                    onChange={handle2FaChange}
                    disabled={is2FADisabled}
                    styleType={InputStyleType.DEFAULT}
                    InputAdornment={
                      <InputAdornment position="end">
                        {isLoading ? <CircularProgress size={20} /> : null}
                      </InputAdornment>
                    }
                  />
                </div>
              }
              {children}
            </StyledFormContainer>
          </Grid>
        </StyledMainContainer>
      </StyledWithdrawalBox>
    </Modal>
  );
};
